import * as React from "react";
import styled, { css, ThemeProvider } from "styled-components";

import Hover from "../../common/components/Hover";
import Icon from "../../common/components/Icon";
import Image, { Image as ImageType } from "../../common/components/Image";
import { media, truncateLines } from "../../common/utils/style";
import MembershipCardStory, { MembershipCardStoryModels } from "./MembershipCardStory";
import Tooltip from "../../common/components/popover/Tooltip";
import Translate from "../../common/components/Translate";
import StarIcon from "../../common/components/StarIcon";

type CompanyMembership = WtdModels.Partial<
  WtdModels.CompanyMembership,
  "position",
  {
    user: WtdModels.Partial<
      WtdModels.User,
      "name" | "user_path",
      {
        avatar: WtdModels.Union<WtdModels.Partial<WtdModels.Image, "id">, ImageType>;
        profile: WtdModels.Partial<WtdModels.Profile, "position", { cover_post: MembershipCardStoryModels["Post"] }>;
      }
    >;
  }
>;

export interface MembershipCardModels {
  CompanyMembership: CompanyMembership;
}

export type Props = {
  membership: CompanyMembership;
  lineColor: string | null;
} & OptionalProps;

export interface OptionalProps {
  editable?: boolean;
  isSelected?: boolean;
  isFeatured?: boolean;
  isEntered?: boolean;
  onMouseEnter?(membership: CompanyMembership): void;
  onFeatured?(membership: CompanyMembership): void;
  onUnfeatured?(membership: CompanyMembership): void;
}

class MembershipCard extends React.PureComponent<Props> {
  public onClickMembership = (event: React.MouseEvent<HTMLElement>) => {
    if (this.props.editable) {
      event.preventDefault();
    }
  };

  public onClickName = (path: string) => {
    if (this.props.editable) {
      window.open(path, "_blank");
    }
  };

  public onClickStar = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    const { isFeatured, membership, onFeatured, onUnfeatured } = this.props;
    if (isFeatured) {
      if (onUnfeatured) {
        onUnfeatured(membership);
      }
    } else {
      if (onFeatured) {
        onFeatured(membership);
      }
    }
  };

  public render() {
    const { user, position } = this.props.membership;
    if (!user) {
      return null;
    }
    const { editable, isFeatured, isSelected } = this.props;
    return (
      <ThemeProvider theme={{ ...this.props }}>
        <Wrapper className="prevent-from-unselecting-memberships">
          <Hover>
            {({ isHovered, ref }) => (
              <Base innerRef={ref} href={user.user_path} isSelected={isSelected} onClick={this.onClickMembership}>
                <AvatarWrapper>
                  <Avatar
                    id={String(user.avatar.id)}
                    size="120x120#"
                    alt={user.name}
                    draggable={false}
                    image={user.avatar}
                    background
                    // FIXME: Enable to extaract this URL from namespace and id
                    fallbackUrl="http://dubpy8abnqmkw.cloudfront.net/images/anonymous/anonymous.png"
                  />
                </AvatarWrapper>
                <MemberDescriptor>
                  <Name onClick={() => this.onClickName(user.user_path)}>{user.name}</Name>
                  <Position>{position}</Position>
                </MemberDescriptor>
                {editable && (
                  <>
                    {isFeatured && (
                      <ToolTipWrapper>
                        <Tooltip render={<Translate id="company.members.remove_from_home" />}>
                          <StyledFilledStarIcon filled={true} onClick={this.onClickStar} />
                        </Tooltip>
                      </ToolTipWrapper>
                    )}
                    {isHovered &&
                      !isFeatured && (
                        <ToolTipWrapper>
                          <Tooltip render={<Translate id="company.members.display_to_home" />}>
                            <StyledStarIcon filled={false} onClick={this.onClickStar} />
                          </Tooltip>
                        </ToolTipWrapper>
                      )}
                    {isHovered && (
                      <HandlerIconWrapper>
                        <Icon name="wt-icon-bers" />
                      </HandlerIconWrapper>
                    )}
                  </>
                )}
              </Base>
            )}
          </Hover>
          {!editable &&
            user.profile.cover_post && (
              <StoryWrapper>
                <MembershipCardStory post={user.profile.cover_post} />
              </StoryWrapper>
            )}
        </Wrapper>
      </ThemeProvider>
    );
  }
}

export default MembershipCard;

const Wrapper = styled.div`
  position: relative;
`;

const Base = styled.a`
  display: block;
  border-radius: 10px;
  margin: 0 auto;
  position: relative;
  text-align: center;
  padding: 20px 0;
  cursor: ${props => (props.theme.editable ? "move" : "pointer")};
  border: 1px solid white;
  transition: background-color 150ms ease-out;
  &:hover {
    background-color: rgba(245, 245, 245, 1);
    text-decoration: none;
  }
  width: 160px;
  @media ${media.large} {
    padding: 20px 0 29px 0;
  }
  ${(p: { isSelected?: boolean }) =>
    p.isSelected
      ? css`
          background-color: rgba(245, 245, 245, 1);
        `
      : ""};
`;

const AvatarWrapper = styled.div`
  margin-bottom: 8px;
  position: relative;
  @media ${media.large} {
    margin: 0 0 19px 0;
  }
`;

const Avatar = styled(Image)`
  display: block;
  margin: 0 auto;
  border-radius: 50%;
  width: 110px;
  height: 110px;
  background-size: cover;
  background-position: center;
`;

const MemberDescriptor = styled.div`
  padding: 0 15px;
  margin-top: 6px;
  text-align: center;
`;

const Name = styled.div`
  display: block;
  font-weight: 600;
  line-height: 1.17;
  color: rgba(0, 0, 0, 0.8);
  font-size: 18px;
  @media ${media.medium} {
    font-size: 14px;
  }
  ${props =>
    props.theme.editable &&
    css`
      &:hover {
        cursor: pointer;
        text-decoration: underline;
      }
    `};
`;

const Position = styled.div`
  font-weight: 400;
  font-size: 13px;
  margin-top: 8px;
  color: rgba(0, 0, 0, 0.4);
  ${truncateLines(1, 13, 1)};
`;

const StoryWrapper = styled.div`
  position: absolute;
  top: calc(131px - 30px);
  right: 25px;
`;

export const HandlerIconWrapper = styled.div`
  position: absolute;
  left: 12px;
  top: 12px;
  color: rgba(0, 0, 0, 0.27);
  cursor: move;
`;

export const ToolTipWrapper = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
`;

const StyledStarIcon = styled(StarIcon)`
  width: 24px;
  height: 24px;
  font-size: 16px;
  border-radius: 50%;
  color: rgba(0, 0, 0, 0.4);
  cursor: pointer;
  &:hover {
    background-color: rgba(238, 238, 238, 1);
  }
`;

export const StyledFilledStarIcon = StyledStarIcon.extend`
  color: rgba(0, 0, 0, 0.702);
`;



// WEBPACK FOOTER //
// ./frontend/src/company_memberships/components/MembershipCard.tsx